'use client';

import { c as _c } from "react/compiler-runtime";
import { Chip } from '@common/components';
import { cn } from '@core/utils';
import { usePathname, useSearchParams } from 'next/navigation';
import { type ComponentProps, Suspense } from 'react';
import { PhotoReviewSwiper } from '@/app/donation/_components/PhotoReviewSwiper';
import { GIFT_REVIEW_SORT_TYPE } from '@/app/donation/constants';
import type { GiftReviewSortType } from '@/app/donation/types';
import { TABS } from '../../../../constants';
import { ResponsiveDivider } from '../../../GiftDetailTemplate/ResponsiveDivider';
import { ReviewSummary } from '../../../ReviewSummary';
import { ReviewList } from './_components/ReviewList';
interface GiftTabContentReviewProps {
  reviewRating?: number;
  reviewCount: number;
  reviewList: ComponentProps<typeof ReviewList>['initialList'];
  reviewTotalCount: number;
  photoReviewList: ComponentProps<typeof PhotoReviewSwiper>['photoReviewList'];
  photoReviewCount: number;
  productNumber: string;
}
const REVIEW_SORT = 'reviewSort';
const toReviewSort = (value: string | null): GiftReviewSortType => value === GIFT_REVIEW_SORT_TYPE.RATING_DESC ? GIFT_REVIEW_SORT_TYPE.RATING_DESC : GIFT_REVIEW_SORT_TYPE.LATEST;
type ReviewSortListProps = Pick<GiftTabContentReviewProps, 'reviewList' | 'reviewTotalCount' | 'productNumber'>;
const ReviewSortList = t0 => {
  const $ = _c(24);
  const {
    reviewSort,
    reviewList,
    reviewTotalCount,
    productNumber
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] !== pathname) {
    t1 = nextSort => {
      const nextSearchParams = new URLSearchParams(window.location.search);
      if (nextSort === GIFT_REVIEW_SORT_TYPE.LATEST) {
        nextSearchParams.delete(REVIEW_SORT);
      } else {
        nextSearchParams.set(REVIEW_SORT, nextSort);
      }
      const search = nextSearchParams.toString();
      history.replaceState({
        historyIndex: history.state?.historyIndex
      }, "", `${pathname}${search ? `?${search}` : ""}`);
    };
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const changeReviewSort = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("flex items-center gap-6 border-b border-Gray-200 px-20 py-12", "mobile-tablet:border-b-0 mobile-tablet:pt-32");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = reviewSort === GIFT_REVIEW_SORT_TYPE.LATEST;
  let t4;
  if ($[3] !== changeReviewSort) {
    t4 = () => changeReviewSort(GIFT_REVIEW_SORT_TYPE.LATEST);
    $[3] = changeReviewSort;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <Chip theme="primary" className="h-36" type="radio" name="gift-review-sort" value={GIFT_REVIEW_SORT_TYPE.LATEST} checked={t3} onCheckedChange={t4}>최신순</Chip>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = reviewSort === GIFT_REVIEW_SORT_TYPE.RATING_DESC;
  let t7;
  if ($[8] !== changeReviewSort) {
    t7 = () => changeReviewSort(GIFT_REVIEW_SORT_TYPE.RATING_DESC);
    $[8] = changeReviewSort;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t6 || $[11] !== t7) {
    t8 = <Chip theme="primary" className="h-36" type="radio" name="gift-review-sort" value={GIFT_REVIEW_SORT_TYPE.RATING_DESC} checked={t6} onCheckedChange={t7}>평점 높은순</Chip>;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t5 || $[14] !== t8) {
    t9 = <div className={t2}>{t5}{t8}</div>;
    $[13] = t5;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== productNumber || $[17] !== reviewList || $[18] !== reviewSort || $[19] !== reviewTotalCount) {
    t10 = <ReviewList initialList={reviewList} totalCount={reviewTotalCount} sortType={reviewSort} productNumber={productNumber} />;
    $[16] = productNumber;
    $[17] = reviewList;
    $[18] = reviewSort;
    $[19] = reviewTotalCount;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t9) {
    t11 = <div className="flex flex-col">{t9}{t10}</div>;
    $[21] = t10;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
};
const SearchParamsReviewSortList = props => {
  const $ = _c(5);
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = toReviewSort(searchParams.get(REVIEW_SORT));
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props || $[3] !== t0) {
    t1 = <ReviewSortList {...props} reviewSort={t0} />;
    $[2] = props;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
export const GiftTabContentReview = t0 => {
  const $ = _c(20);
  let photoReviewCount;
  let photoReviewList;
  let reviewCount;
  let reviewRating;
  let reviewSortListProps;
  if ($[0] !== t0) {
    ({
      reviewRating,
      reviewCount,
      photoReviewList,
      photoReviewCount,
      ...reviewSortListProps
    } = t0);
    $[0] = t0;
    $[1] = photoReviewCount;
    $[2] = photoReviewList;
    $[3] = reviewCount;
    $[4] = reviewRating;
    $[5] = reviewSortListProps;
  } else {
    photoReviewCount = $[1];
    photoReviewList = $[2];
    reviewCount = $[3];
    reviewRating = $[4];
    reviewSortListProps = $[5];
  }
  let t1;
  if ($[6] !== reviewCount || $[7] !== reviewRating) {
    t1 = <ReviewSummary reviewRating={reviewRating} reviewCount={reviewCount} className="mobile-tablet:mx-16" />;
    $[6] = reviewCount;
    $[7] = reviewRating;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== photoReviewCount || $[10] !== photoReviewList || $[11] !== reviewSortListProps.productNumber) {
    t2 = <PhotoReviewSwiper photoReviewList={photoReviewList} photoReviewCount={photoReviewCount} productNumber={reviewSortListProps.productNumber} className="mobile-tablet:px-20" />;
    $[9] = photoReviewCount;
    $[10] = photoReviewList;
    $[11] = reviewSortListProps.productNumber;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ResponsiveDivider className="h-0" />;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== reviewSortListProps) {
    t4 = <Suspense fallback={<ReviewSortList {...reviewSortListProps} reviewSort={GIFT_REVIEW_SORT_TYPE.LATEST} />}><SearchParamsReviewSortList {...reviewSortListProps} /></Suspense>;
    $[14] = reviewSortListProps;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== t1 || $[17] !== t2 || $[18] !== t4) {
    t5 = <section className="flex flex-col pt-32" id={TABS.REVIEW}>{t1}{t2}{t3}{t4}</section>;
    $[16] = t1;
    $[17] = t2;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
};